<script setup lang="ts">
import type { UserHostInvitee } from "@/models"
import { computed, useTemplateRef } from "vue"
import { Menu, MenuItem } from "@/components/legacy"
import { Button } from "@/components/ui"
import { useTranslation } from "@/composables"

const { hostInvitee } = defineProps<UserHostActionsMenuProps>()
const emit = defineEmits<{
  (e: "transferOwnershipTo", hostInvitee?: UserHostInvitee): void
  (e: "reinvite", hostInvitee?: UserHostInvitee): void
  (e: "remove", hostInvitee?: UserHostInvitee): void
}>()

interface UserHostActionsMenuProps {
  hostInvitee: UserHostInvitee
}

const { t } = useTranslation()

const menuRef = useTemplateRef("menuRef")

function onItemClick(cb: (v?: number) => void, dismiss: boolean = false) {
  if (dismiss) {
    menuRef.value?.toggle()
  }

  cb()
}

const displayMenu = computed(() => hostInvitee.canBeReinvited || hostInvitee.canBeRemoved || hostInvitee.canTransferOwnership)
</script>

<template>
  <Menu v-if="displayMenu" ref="menuRef" data-test="actions-menu">
    <template #activator="{ toggle }">
      <Button prefix-icon="MenuEllipsisHorizontal" variant="icon" @click="toggle">
        <span class="sr-only">{{ t("event_co_host_list_page.actions_menu.sr") }}</span>
      </Button>
    </template>

    <template #default="{ toggle, isOpen }">
      <div v-show="isOpen" class="flex flex-col gap-2">
        <MenuItem
          v-if="hostInvitee.canTransferOwnership"
          :item="{
            key: 'transferOwnership',
            label: t('event_co_host_list_page.actions_menu.transfer_ownership_to'),
            onClick: () => onItemClick(() => {
              emit('transferOwnershipTo', hostInvitee)
              toggle()
            }),
          }"
        />

        <MenuItem
          v-if="hostInvitee.canBeReinvited"
          :item="{
            key: 'reinvite',
            label: t('event_co_host_list_page.actions_menu.reinvite'),
            onClick: () => onItemClick(() => {
              emit('reinvite', hostInvitee)
              toggle()
            }),
          }"
        />

        <MenuItem
          v-if="hostInvitee.canBeRemoved"
          :item="{
            key: 'remove',
            label: t('event_co_host_list_page.actions_menu.remove'),
            onClick: () => onItemClick(() => {
              emit('remove', hostInvitee)
              toggle()
            }),
            variant: 'danger',
          }"
        />
      </div>
    </template>
  </Menu>
</template>
